import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Users, Home, Bell, MessageSquare, Briefcase, LogOut, User, Shield, Menu, X, Sun, Moon, Search, ArrowLeft } from 'lucide-react';
import api from '../services/api';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { useNotifications } from '../context/NotificationContext';
import MobileSidebar from './MobileSidebar';

function Navbar({ user, onLogout }) {
  const [profile, setProfile] = useState(null);
  const { theme, toggleTheme } = useTheme();
  const { language, toggleLanguage } = useLanguage();
  const { unreadCount, unreadMessages, setUnreadCount, setUnreadMessages, refreshCounts } = useNotifications();

  // Need user profile info for avatars
  useEffect(() => {
    if (user?.id) {
      api.get(`/users/${user.id}`).then(res => setProfile(res.data)).catch(console.error);
    }
  }, [user?.id]);

  const handleNotificationsClick = async () => {
    setUnreadCount(0);
    try {
      await api.put('/users/me/notifications/read-all');
    } catch (err) {
      console.error('Failed to mark all as read');
    }
    refreshCounts();
  };

  const handleMessagesClick = () => {
    setUnreadMessages(0);
    refreshCounts();
  };

  const location = useLocation();
  const navigate = useNavigate();
  const isMessagesView = location.pathname.startsWith('/messages');

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const role = user?.role || 'guest';

  // Build nav links based on role
  const navLinks = [];

  // Feed — everyone
  navLinks.push({ to: '/', icon: Home, label: 'Feed' });

  // Directory — everyone (guests see limited, handled in Members page)
  navLinks.push({ to: '/members', icon: Users, label: 'Directory' });

  // Opportunities — everyone except guests
  if (role !== 'guest') {
    navLinks.push({ to: '/opportunities', icon: Briefcase, label: 'Opportunities' });
  }

  // Messages — everyone except guests
  if (role !== 'guest') {
    navLinks.push({ to: '/messages', icon: MessageSquare, label: 'Messages', badge: unreadMessages, onClick: handleMessagesClick });
  }

  // Notifications — everyone
  navLinks.push({ to: '/notifications', icon: Bell, label: 'Notifications', badge: unreadCount, onClick: handleNotificationsClick });

  const handleToggle = () => setIsMenuOpen(!isMenuOpen);
  const closeMenu = () => setIsMenuOpen(false);

  return (
    <>
      {/* ─── DESKTOP NAVBAR ─── */}
      <nav className="glass-nav desktop-only">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
          <Link to="/" onClick={closeMenu} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', textDecoration: 'none' }}>
            <div style={{
              background: 'var(--accent-primary)',
              width: '40px', height: '40px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: 'var(--glow-indigo)'
            }}>
              <span style={{ fontWeight: 900, color: 'var(--btn-text)', fontSize: '1.4rem' }}>A</span>
            </div>
            <span className="nav-logo-text" style={{
              fontFamily: 'Outfit', fontWeight: 800, fontSize: '1.4rem', color: 'var(--text-heading)',
              letterSpacing: '-0.03em'
            }}>
              AP <span style={{ color: 'var(--accent-primary)' }}>NEO</span>
            </span>
          </Link>

          {/* Desktop Links */}
          <div className="nav-links-container" style={{ display: 'flex', gap: '0.5rem', background: 'var(--glass-bg)', padding: '0.25rem', borderRadius: '999px', border: '1px solid var(--glass-border)' }}>
            {navLinks.map((link) => {
              const isActive = location.pathname === link.to;
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => {
                    if (link.onClick) link.onClick();
                  }}
                  className="nav-link-item"
                  title={link.label}
                  style={{
                    padding: '0.6rem 1.25rem',
                    borderRadius: '999px',
                    display: 'flex',
                    alignItems: 'center',
                    position: 'relative',
                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                    background: isActive ? 'rgba(99, 102, 241, 0.15)' : 'transparent',
                    color: isActive ? 'var(--text-heading)' : 'var(--text-body)',
                    boxShadow: isActive ? 'var(--glow-indigo)' : 'none',
                    border: isActive ? '1px solid rgba(99, 102, 241, 0.3)' : '1px solid transparent'
                  }}
                >
                  <link.icon size={20} />
                  {link.badge > 0 && (
                    <span style={{
                      position: 'absolute',
                      top: '-4px',
                      right: '-4px',
                      background: 'var(--accent-magenta)',
                      color: 'var(--btn-text)',
                      fontSize: '0.65rem',
                      fontWeight: 800,
                      minWidth: '18px',
                      height: '18px',
                      borderRadius: '999px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 0 8px var(--accent-magenta)'
                    }}>
                      {typeof link.badge === 'object' ? 0 : (link.badge > 9 ? '9+' : link.badge)}
                    </span>
                  )}
                </Link>
              );
            })}
            {role === 'ap_admin' && (
              <Link
                to="/admin"
                className="nav-link-item"
                title="Admin"
                style={{
                  padding: '0.6rem 1.25rem',
                  borderRadius: '999px',
                  display: 'flex',
                  alignItems: 'center',
                  position: 'relative',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  background: location.pathname === '/admin' ? 'rgba(244, 114, 182, 0.15)' : 'transparent',
                  color: location.pathname === '/admin' ? 'var(--accent-magenta)' : 'var(--text-body)',
                  boxShadow: location.pathname === '/admin' ? '0 0 15px rgba(244, 114, 182, 0.3)' : 'none',
                  border: location.pathname === '/admin' ? '1px solid rgba(244, 114, 182, 0.3)' : '1px solid transparent'
                }}
              >
                <Shield size={20} />
              </Link>
            )}
          </div>

          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
            <button
              onClick={toggleTheme}
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid var(--glass-border)',
                borderRadius: '12px',
                width: '40px', height: '40px',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', color: 'var(--accent-primary)',
                transition: 'all 0.3s ease',
                boxShadow: 'var(--shadow-inset)'
              }}
              title={`Switch to ${theme === 'dark' ? 'Light' : 'Dark'} Mode`}
            >
              {theme === 'dark' ? <Sun size={20} /> : <Moon size={20} />}
            </button>

            <div
              onClick={toggleLanguage}
              style={{
                background: 'rgba(255, 255, 255, 0.05)',
                border: '1px solid var(--glass-border)',
                borderRadius: '12px',
                height: '40px', padding: '0 0.75rem',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                cursor: 'pointer', color: 'var(--text-muted)',
                transition: 'all 0.3s ease', fontSize: '0.75rem', fontWeight: 900
              }}
              className="hover-bright desktop-only"
            >
              <span style={{ color: language === 'en' ? 'var(--accent-primary)' : 'inherit' }}>EN</span>
              <span style={{ margin: '0 4px', opacity: 0.3 }}>/</span>
              <span style={{ color: language === 'gr' ? 'var(--accent-primary)' : 'inherit' }}>ΕΛ</span>
            </div>

            <Link to={`/profile/${user.id}`} className="desktop-only" style={{
              display: 'flex', alignItems: 'center', gap: '0.75rem', textDecoration: 'none',
              padding: '0.5rem 1rem', borderRadius: '14px', background: 'var(--bg-card)',
              border: '1px solid var(--glass-border)',
              boxShadow: 'var(--shadow-inset)'
            }}>
              <User size={18} color={'var(--accent-primary)'} />
              <span className="user-profile-text" style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--text-heading)' }}>{typeof user.username === 'object' ? 'User' : user.username}</span>
            </Link>

            <button
              className="mobile-only"
              onClick={handleToggle}
              style={{
                background: 'transparent', border: 'none', color: 'var(--text-primary)',
                display: 'none', cursor: 'pointer', padding: '0.5rem',
                position: 'relative'
              }}
            >
              {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
              {!isMenuOpen && unreadCount > 0 && (
                <span style={{
                  position: 'absolute',
                  top: '0',
                  right: '0',
                  background: 'var(--accent-magenta)',
                  width: '10px',
                  height: '10px',
                  borderRadius: '50%',
                  boxShadow: '0 0 8px var(--accent-magenta)'
                }}></span>
              )}
            </button>

            <button
              className="desktop-only"
              onClick={onLogout}
              style={{
                background: 'rgba(244, 114, 182, 0.1)', border: '1px solid rgba(244, 114, 182, 0.2)',
                color: 'var(--accent-magenta)', padding: '0.4rem', borderRadius: '10px'
              }}
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </nav>

      {/* ─── MOBILE TOP NAVBAR ─── */}
      <nav className="mobile-only" style={{
        position: 'fixed', top: 0, left: 0, right: 0, height: '56px',
        background: 'var(--bg-card)', borderBottom: '1px solid var(--glass-border)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '0 1rem', zIndex: 1000
      }}>
        {/* Left: Avatar -> Opens Sidebar or Back Button */}
        {isMessagesView ? (
          <button
            onClick={() => navigate(-1)}
            style={{
              background: 'transparent', border: 'none', padding: '0 0.5rem 0 0', cursor: 'pointer',
              display: 'flex', alignItems: 'center', color: 'var(--text-primary)'
            }}
          >
            <ArrowLeft size={24} />
          </button>
        ) : (
          <button
            onClick={() => setIsMenuOpen(true)}
            style={{
              background: 'transparent', border: 'none', padding: 0, cursor: 'pointer',
              position: 'relative', display: 'flex', alignItems: 'center'
            }}
          >
            <div style={{ width: '32px', height: '32px', borderRadius: '8px', overflow: 'hidden', border: '1px solid var(--glass-border)' }}>
              {profile?.avatar_url ? (
                <img src={profile.avatar_url} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                <div style={{ width: '100%', height: '100%', background: 'var(--bg-deep)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, color: 'var(--accent-primary)', fontSize: '0.8rem' }}>
                  {user?.username?.[0]?.toUpperCase()}
                </div>
              )}
            </div>
          </button>
        )}

        {/* Center: Search input (simplified LinkedIn style) */}
        <div style={{ flex: 1, margin: '0 1rem', position: 'relative', display: 'flex', minWidth: 0 }}>
          <div style={{
            background: 'var(--bg-deep)', borderRadius: '6px', height: '32px',
            display: 'flex', alignItems: 'center', padding: '0 0.5rem', gap: '0.5rem',
            border: '1px solid var(--glass-border)', width: '100%', minWidth: 0
          }}>
            <Search size={14} color="var(--text-muted)" style={{ flexShrink: 0 }} />
            <input type="text" placeholder="Search" style={{ background: 'transparent', border: 'none', outline: 'none', color: 'var(--text-primary)', fontSize: '0.85rem', width: '100%', minWidth: 0 }} />
          </div>
        </div>

        {/* Right: Messages */}
        <Link
          to="/messages"
          onClick={handleMessagesClick}
          style={{ position: 'relative', color: 'var(--text-muted)', display: 'flex' }}
        >
          <MessageSquare size={22} />
          {unreadMessages > 0 && (
            <span style={{
              position: 'absolute', top: '-4px', right: '-6px',
              background: 'var(--accent-magenta)', color: 'white',
              fontSize: '0.6rem', fontWeight: 900,
              minWidth: '16px', height: '16px', borderRadius: '8px',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 0 6px var(--accent-magenta)'
            }}>
              {unreadMessages > 9 ? '9+' : unreadMessages}
            </span>
          )}
        </Link>
      </nav>

      {/* ─── MOBILE SIDEBAR ─── */}
      <MobileSidebar
        isOpen={isMenuOpen}
        onClose={closeMenu}
        user={user}
        profile={profile}
        onLogout={onLogout}
      />
    </>
  );
}

export default Navbar;
